import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  ComputerDesktopIcon,
  CpuChipIcon,
  GlobeAltIcon,
  MapPinIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  CommandLineIcon,
  ServerIcon,
  TrashIcon,
  ArrowPathIcon,
  WindowIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../../services/api";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import EditBranchModal from "../../components/machines/EditBranchModal";
import {
  Machine,
  HardwareData,
  WebHistory,
  Location,
  InstalledProgram,
  Device,
  StorageDevice,
  OpenApplication,
} from "../../types";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

type TabType =
  | "overview"
  | "hardware"
  | "software"
  | "devices"
  | "web-history"
  | "open-applications"
  | "location";

export default function MachineDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const [machine, setMachine] = useState<Machine | null>(null);
  const [hardwareData, setHardwareData] = useState<HardwareData[]>([]);
  const [installedPrograms, setInstalledPrograms] = useState<
    InstalledProgram[]
  >([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [storageDevices, setStorageDevices] = useState<StorageDevice[]>([]);
  const [webHistory, setWebHistory] = useState<WebHistory[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [openApplications, setOpenApplications] = useState<OpenApplication[]>(
    [],
  );
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  // Edit state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditBranchModalOpen, setIsEditBranchModalOpen] = useState(false);
  const [observation, setObservation] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Delete state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Force sync state
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState("");

  // Recalculate location state
  const [locationService, setLocationService] = useState<string>("auto");
  const [isRecalculating, setIsRecalculating] = useState(false);

  const navigate = useNavigate();

  // Helper to get dates
  const getToday = () => new Date().toISOString().split("T")[0];
  const getYesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split("T")[0];
  };

  // Web history filters
  const [webSearchTerm, setWebSearchTerm] = useState("");
  const [webStatusFilter, setWebStatusFilter] = useState("all");
  const [webStartDate, setWebStartDate] = useState(getYesterday());
  const [webEndDate, setWebEndDate] = useState(getToday());

  // Software filters
  const [softwareSearchTerm, setSoftwareSearchTerm] = useState("");

  // Open applications filters
  const [appProcessName, setAppProcessName] = useState("");
  const [appWindowTitle, setAppWindowTitle] = useState("");
  const [appStartDate, setAppStartDate] = useState(getYesterday());
  const [appEndDate, setAppEndDate] = useState(getToday());
  const [appCurrentPage, setAppCurrentPage] = useState(1);
  const [appTotalPages, setAppTotalPages] = useState(1);
  const [appTotal, setAppTotal] = useState(0);
  const [isLoadingApps, setIsLoadingApps] = useState(false);

  const loadMachineData = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const [
        machineResponse,
        machineDetailsResponse,
        hardwareResponse,
        programsResponse,
        devicesResponse,
        webResponse,
        locationData,
      ] = await Promise.all([
        apiService.getMachine(id),
        apiService.getMachineDetails(id),
        apiService.getMachineHardware(id),
        apiService.getMachineInstalledPrograms(id),
        apiService.getMachineDevices(id),
        apiService.getMachineWebHistory(id, {
          limit: 50,
          startDate: webStartDate,
          endDate: webEndDate,
        }),
        apiService.getMachineLocation(id),
      ]);

      const machineData = machineDetailsResponse?.machine || machineResponse;
      if (typeof machineData.os_info === "string") {
        try {
          machineData.os_info = JSON.parse(machineData.os_info);
        } catch (e) {
          console.error("Error parsing os_info:", e);
          machineData.os_info = {};
        }
      }
      setMachine(machineData);
      setHardwareData(hardwareResponse);
      setInstalledPrograms(programsResponse);
      setDevices(devicesResponse);
      setStorageDevices(machineDetailsResponse?.storage_devices || []);
      setWebHistory(webResponse.data);
      setLocations(locationData);
    } catch (err: any) {
      setError("Erro ao carregar dados da máquina");
      console.error("Machine details error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [id, webStartDate, webEndDate]);

  const loadMachineDetailsForRefresh = useCallback(async () => {
    if (!id) return;
    try {
      const [
        machineResponse,
        hardwareResponse,
        storageResponse,
        locationResponse,
      ] = await Promise.all([
        apiService.getMachine(id),
        apiService.getMachineHardware(id),
        apiService.getMachineDetails(id), // To get storage_devices
        apiService.getMachineLocation(id),
      ]);

      const machineData = machineResponse;
      if (typeof machineData.os_info === "string") {
        try {
          machineData.os_info = JSON.parse(machineData.os_info);
        } catch (e) {
          console.error("Error parsing os_info:", e);
          machineData.os_info = {};
        }
      }
      setMachine(machineData);
      setHardwareData(hardwareResponse);
      setStorageDevices(storageResponse?.storage_devices || []);
      setLocations(locationResponse);
    } catch (err: any) {
      console.error("Error refreshing machine details:", err);
    }
  }, [id]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      await loadMachineData();
      setIsLoading(false);
    };
    init();

    // Auto-refresh data every 30 seconds to keep "Last Seen" and status updated
    if (id) {
      const intervalId = setInterval(loadMachineDetailsForRefresh, 30000);
      return () => clearInterval(intervalId);
    }
  }, [id, loadMachineData, loadMachineDetailsForRefresh]);

  const loadOpenApplications = useCallback(
    async (page: number = 1) => {
      if (!id) return;

      try {
        setIsLoadingApps(true);
        const params: any = {
          page,
          per_page: 50,
        };

        if (appStartDate) params.start_date = appStartDate;
        if (appEndDate) params.end_date = appEndDate;
        if (appProcessName) params.process_name = appProcessName;
        if (appWindowTitle) params.window_title = appWindowTitle;

        const response = await apiService.getMachineOpenApplications(
          id,
          params,
        );
        setOpenApplications(response.data);
        setAppCurrentPage(response.pagination.current_page);
        setAppTotalPages(response.pagination.last_page);
        setAppTotal(response.pagination.total);
      } catch (err) {
        console.error("Error loading open applications:", err);
        setOpenApplications([]);
      } finally {
        setIsLoadingApps(false);
      }
    },
    [id, appStartDate, appEndDate, appProcessName, appWindowTitle],
  );

  // Load open applications when tab is active
  useEffect(() => {
    if (activeTab === "open-applications" && id) {
      loadOpenApplications();
    }
  }, [activeTab, id, loadOpenApplications]);

  const filterOpenApplications = async () => {
    setAppCurrentPage(1);
    await loadOpenApplications(1);
  };

  // Filter web history
  const filterWebHistory = async () => {
    if (!id) return;

    try {
      const params = {
        search: webSearchTerm || undefined,
        status: webStatusFilter !== "all" ? webStatusFilter : undefined,
        startDate: webStartDate || undefined,
        endDate: webEndDate || undefined,
        limit: 50,
      };

      const response = await apiService.getMachineWebHistory(id, params);
      setWebHistory(response.data);
    } catch (err) {
      console.error("Error filtering web history:", err);
    }
  };

  const handleUpdateMachine = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!machine) return;

    try {
      setIsSaving(true);

      const updateData: any = {};
      const observationValue = observation || "";
      updateData.observation =
        observationValue.trim() === "" ? "" : observationValue.trim();

      await apiService.updateMachine(machine.id, updateData);

      await new Promise((resolve) => setTimeout(resolve, 300));
      await loadMachineData();

      setObservation("");
      setIsEditModalOpen(false);
    } catch (err: any) {
      console.error("Error updating machine:", err);
      const errorMessage =
        err.response?.data?.message || "Erro ao atualizar máquina";
      alert(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRecalculateLocation = async () => {
    if (!machine) return;

    setIsRecalculating(true);
    try {
      const response = await apiService.recalculateMachineLocation(
        machine.id,
        locationService,
      );

      if (response.success) {
        // Reload machine data including locations
        await loadMachineData();
        // Also explicitly reload locations
        try {
          const locationResponse = await apiService.getMachineLocation(machine.id);
          setLocations(locationResponse);
        } catch (err) {
          console.error("Failed to reload locations:", err);
        }
        alert(
          `Localização recalculada com sucesso!\nMétodo: ${
            response.method
          }\nCidade: ${response.location.city || "N/A"}`,
        );
      } else {
        alert(
          `Erro ao recalcular localização: ${
            response.message || "Erro desconhecido"
          }`,
        );
      }
    } catch (err: any) {
      console.error("Failed to recalculate location:", err);
      alert(`Erro ao recalcular localização: ${err.message || "Erro de rede"}`);
    } finally {
      setIsRecalculating(false);
    }
  };

  const handleForceSync = async () => {
    if (!machine || !id) return;

    try {
      setIsSyncing(true);
      setSyncMessage("");

      const response = await apiService.forceSyncMachine(id);

      if (response.success) {
        setSyncMessage(response.message);
        
        // Reload machine data including locations after sync
        setTimeout(async () => {
          await loadMachineDetailsForRefresh();
          // Also explicitly reload locations
          try {
            const locationResponse = await apiService.getMachineLocation(id);
            setLocations(locationResponse);
          } catch (err) {
            console.error("Failed to reload locations:", err);
          }
        }, 2000); // Wait 2 seconds for sync to complete

        // Clear message after 5 seconds
        setTimeout(() => {
          setSyncMessage("");
        }, 5000);
      } else {
        setSyncMessage("Erro ao solicitar sincronização");
        setTimeout(() => {
          setSyncMessage("");
        }, 5000);
      }
    } catch (err: any) {
      setSyncMessage(
        err.response?.data?.message || "Erro ao solicitar sincronização",
      );
      setTimeout(() => {
        setSyncMessage("");
      }, 5000);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDeleteMachine = async () => {
    if (!machine) return;

    try {
      setIsDeleting(true);
      await apiService.deleteMachine(machine.id);
      navigate("/machines");
    } catch (err) {
      console.error("Error deleting machine:", err);
      alert("Erro ao excluir máquina");
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "online":
        return "text-green-600 bg-green-100";
      case "offline":
        return "text-red-600 bg-red-100";
      case "warning":
        return "text-yellow-600 bg-yellow-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const tabs = [
    { id: "overview", name: "Visão Geral", icon: ComputerDesktopIcon },
    { id: "hardware", name: "Hardware", icon: CpuChipIcon },
    { id: "software", name: "Software", icon: CommandLineIcon },
    { id: "devices", name: "Dispositivos", icon: ServerIcon },
    { id: "web-history", name: "Histórico Web", icon: GlobeAltIcon },
    { id: "open-applications", name: "Softwares Abertos", icon: WindowIcon },
    { id: "location", name: "Localização", icon: MapPinIcon },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !machine) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-700">{error || "Máquina não encontrada"}</div>
        <Link
          to="/machines"
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Voltar para lista de máquinas
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to="/machines"
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-1" />
          Voltar
        </Link>
        <div className="flex-1">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                {machine.name || machine.agent_id}
              </h1>
              <div className="mt-0.5 flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
                <span>Usuário: {machine.user_name || "N/A"}</span>
                {machine.branch && (
                  <span className="flex items-center">
                    Filial: {machine.branch}
                    <button
                      onClick={() => setIsEditBranchModalOpen(true)}
                      className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                      title="Alterar Filial"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </span>
                )}
                {!machine.branch && (
                  <span className="flex items-center">
                    <button
                      onClick={() => setIsEditBranchModalOpen(true)}
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-xs flex items-center focus:outline-none"
                    >
                      <PencilIcon className="h-3 w-3 mr-1" />
                      Definir Filial
                    </button>
                  </span>
                )}
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                    machine.status,
                  )}`}
                >
                  {machine.status}
                </span>
              </div>
              {syncMessage && (
                <div
                  className={`mt-2 text-sm ${
                    syncMessage.includes("Erro")
                      ? "text-red-600 dark:text-red-400"
                      : "text-green-600 dark:text-green-400"
                  }`}
                >
                  {syncMessage}
                </div>
              )}
            </div>
            <button
              onClick={handleForceSync}
              disabled={isSyncing || machine.status === "offline"}
              className="inline-flex items-center px-2.5 py-1.5 border border-blue-300 shadow-sm text-xs leading-4 font-medium rounded text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-blue-400 dark:border-blue-600 dark:hover:bg-gray-600"
            >
              {isSyncing ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="ml-1.5">Sincronizando...</span>
                </>
              ) : (
                <>
                  <ArrowPathIcon className="h-3.5 w-3.5 mr-1.5" />
                  Sincronizar Agora
                </>
              )}
            </button>
            <button
              onClick={() => {
                const initialObservation = machine.observation || "";
                setObservation(initialObservation);
                setIsEditModalOpen(true);
              }}
              className="ml-2 inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs leading-4 font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
            >
              <PencilIcon className="h-3.5 w-3.5 mr-1.5" />
              Editar
            </button>
            <button
              onClick={() => setIsDeleteModalOpen(true)}
              className="ml-2 inline-flex items-center px-2.5 py-1.5 border border-red-300 shadow-sm text-xs leading-4 font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:bg-gray-700 dark:text-red-400 dark:border-red-600 dark:hover:bg-gray-600"
            >
              <TrashIcon className="h-3.5 w-3.5 mr-1.5" />
              Excluir
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                    : "border-transparent text-gray-700 hover:text-gray-900 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600"
                } whitespace-nowrap py-1.5 px-1 border-b-2 font-medium text-xs flex items-center`}
              >
                <Icon className="w-3.5 h-3.5 mr-1.5" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white shadow rounded-lg dark:bg-gray-800">
        {activeTab === "overview" && (
          <div className="p-4">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Informações Gerais
            </h3>
            <dl className="grid grid-cols-1 gap-x-3 gap-y-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Nome da Máquina
                </dt>
                <dd className="mt-0.5 text-xs text-gray-900 dark:text-white">
                  {machine.name || "N/A"}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  ID do Agente
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                  {machine.agent_id || "N/A"}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Versão do Agente
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                  {machine.agent_version || "N/A"}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Usuário Logado
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                  {machine.user_name || "N/A"}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Filial / Local
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                  {machine.branch || "N/A"}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Status
                </dt>
                <dd className="mt-1">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                      machine.status,
                    )}`}
                  >
                    {machine.status}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Última Atividade
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                  {machine.last_seen
                    ? new Date(
                        machine.last_seen.endsWith("Z")
                          ? machine.last_seen
                          : machine.last_seen + "Z",
                      ).toLocaleString("pt-BR", {
                        timeZone: "America/Sao_Paulo",
                      })
                    : "N/A"}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Criado em
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                  {machine.created_at
                    ? new Date(
                        machine.created_at.endsWith("Z")
                          ? machine.created_at
                          : machine.created_at + "Z",
                      ).toLocaleString("pt-BR", {
                        timeZone: "America/Sao_Paulo",
                      })
                    : "N/A"}
                </dd>
              </div>
              {machine.os_info && Object.keys(machine.os_info).length > 0 && (
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Versão do Sistema Operacional
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 bg-gray-50 rounded-md p-4 border border-gray-200 dark:bg-gray-700 dark:text-white dark:border-gray-600">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Show os_version first if available */}
                      {machine.os_info.os_version && (
                        <div>
                          <span className="font-medium text-gray-500 dark:text-gray-400">
                            Versão:
                          </span>
                          <span className="ml-2 text-gray-900 dark:text-white">
                            {String(machine.os_info.os_version)}
                          </span>
                        </div>
                      )}
                      {/* Show other relevant OS info, excluding uptime */}
                      {Object.entries(machine.os_info)
                        .filter(
                          ([key]) => key !== "uptime" && key !== "os_version",
                        )
                        .map(([key, value]) => (
                          <div key={key}>
                            <span className="font-medium text-gray-500 dark:text-gray-400 capitalize">
                              {key.replace(/_/g, " ")}:
                            </span>
                            <span className="ml-2 text-gray-900 dark:text-white">
                              {String(value)}
                            </span>
                          </div>
                        ))}
                    </div>
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {activeTab === "hardware" && (
          <div className="p-4">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Configuração de Hardware
            </h3>

            {/* Current Hardware Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 dark:bg-blue-900/20 dark:border-blue-800">
              <h4 className="text-md font-medium text-blue-900 dark:text-blue-100 mb-3">
                Hardware Atual
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400 block">
                    Processador (CPU)
                  </span>
                  <span className="text-sm text-gray-900 dark:text-white">
                    {machine.cpu || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400 block">
                    Memória RAM
                  </span>
                  <span className="text-sm text-gray-900 dark:text-white">
                    {machine.ram || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400 block">
                    Placa de Vídeo (GPU)
                  </span>
                  <span className="text-sm text-gray-900 dark:text-white">
                    {machine.gpu || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400 block">
                    Placa Mãe
                  </span>
                  <span className="text-sm text-gray-900 dark:text-white">
                    {machine.motherboard || "N/A"}
                  </span>
                </div>
              </div>
            </div>

            {/* Storage Devices Section */}
            {storageDevices.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 dark:bg-gray-800 dark:border-gray-700">
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                  Armazenamento (HDD/SSD)
                </h4>
                <div className="space-y-4">
                  {storageDevices.map((storage) => (
                    <div
                      key={storage.id}
                      className="border border-gray-200 rounded-lg p-4 dark:border-gray-700"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {storage.mountpoint || storage.device}
                          </span>
                          {storage.type && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {storage.type}
                            </span>
                          )}
                        </div>
                        {storage.model && (
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {storage.model}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">
                            Total
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {storage.formatted_total ||
                              `${(storage.total_bytes / 1024 ** 3).toFixed(
                                2,
                              )} GB`}
                          </span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">
                            Usado
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {storage.formatted_used ||
                              `${(storage.used_bytes / 1024 ** 3).toFixed(
                                2,
                              )} GB`}
                          </span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">
                            Livre
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {storage.formatted_free ||
                              `${(storage.free_bytes / 1024 ** 3).toFixed(
                                2,
                              )} GB`}
                          </span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">
                            Uso
                          </span>
                          <div className="flex items-center space-x-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                              <div
                                className={`h-2 rounded-full ${
                                  (Number(storage.usage_percent) || 0) > 90
                                    ? "bg-red-500"
                                    : (Number(storage.usage_percent) || 0) > 75
                                      ? "bg-yellow-500"
                                      : "bg-green-500"
                                }`}
                                style={{
                                  width: `${
                                    Number(storage.usage_percent) || 0
                                  }%`,
                                }}
                              />
                            </div>
                            <span className="text-xs font-medium text-gray-900 dark:text-white">
                              {(Number(storage.usage_percent) || 0).toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </div>
                      {storage.filesystem && (
                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          Sistema de arquivos: {storage.filesystem}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
              Histórico de Alterações
            </h4>
            {hardwareData.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">
                Nenhum histórico de hardware disponível
              </p>
            ) : (
              <div className="space-y-6 max-h-[600px] overflow-y-auto pr-2">
                {hardwareData
                  .filter((hardware) => hardware.data_type !== "network")
                  .map((hardware) => (
                    <div
                      key={hardware.id}
                      className="border border-gray-200 rounded-lg p-4 dark:border-gray-700"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-md font-medium text-gray-900 dark:text-white capitalize">
                          {hardware.data_type}
                        </h4>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(hardware.timestamp).toLocaleString("pt-BR")}
                        </span>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-md dark:bg-gray-700">
                        <pre className="text-xs overflow-x-auto text-gray-800 dark:text-gray-300">
                          {JSON.stringify(hardware.data, null, 2)}
                        </pre>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "software" && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Software Instalado
              </h3>
            </div>

            {/* Software Filter */}
            <div className="mb-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={softwareSearchTerm}
                  onChange={(e) => setSoftwareSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  placeholder="Buscar software por nome..."
                />
              </div>
            </div>

            {installedPrograms.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">
                Nenhum software encontrado
              </p>
            ) : (
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Nome
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Versão
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Fornecedor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Data Instalação
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                    {installedPrograms
                      .filter((p) =>
                        p.name
                          .toLowerCase()
                          .includes(softwareSearchTerm.toLowerCase()),
                      )
                      .map((program) => (
                        <tr key={program.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {program.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {program.version || "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {program.publisher || program.vendor || "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {program.install_date
                              ? new Date(
                                  program.install_date,
                                ).toLocaleDateString("pt-BR")
                              : "-"}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === "devices" && (
          <div className="p-4">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Dispositivos Conectados
            </h3>
            {devices.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">
                Nenhum dispositivo encontrado
              </p>
            ) : (
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Nome
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Fabricante
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                    {devices.map((device) => (
                      <tr key={device.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {device.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {device.type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {device.manufacturer || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {(() => {
                            const status = device.status || "";
                            const statusLower = status.toLowerCase();
                            
                            if (statusLower === "conectado" || statusLower === "ok" || statusLower === "connected") {
                              return (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                  Conectado
                                </span>
                              );
                            } else if (statusLower === "offline" || statusLower === "desconectado") {
                              return (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                  Offline
                                </span>
                              );
                            } else if (status) {
                              // Fallback for any other status (display as-is)
                              return (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                                  {status}
                                </span>
                              );
                            } else {
                              return <span>-</span>;
                            }
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === "web-history" && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Histórico de Navegação
              </h3>
            </div>

            {/* Web History Filters */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6 dark:bg-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Buscar URL ou Título
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={webSearchTerm}
                      onChange={(e) => setWebSearchTerm(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" && filterWebHistory()
                      }
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                      placeholder="Buscar por URL ou título..."
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    value={webStatusFilter}
                    onChange={(e) => setWebStatusFilter(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  >
                    <option value="all">Todos</option>
                    <option value="normal">Normal (Permitido)</option>
                    <option value="blocked">Bloqueado</option>
                    <option value="site_proibido">Site Proibido</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Data Inicial
                  </label>
                  <input
                    type="date"
                    value={webStartDate}
                    onChange={(e) => setWebStartDate(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && filterWebHistory()}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Data Final
                  </label>
                  <input
                    type="date"
                    value={webEndDate}
                    onChange={(e) => setWebEndDate(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && filterWebHistory()}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={filterWebHistory}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                >
                  Filtrar
                </button>
              </div>
            </div>

            {webHistory.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">
                Nenhum histórico de navegação disponível
              </p>
            ) : (
              <div className="overflow-y-auto max-h-[600px] shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        URL
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Título
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Navegador
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Data/Hora
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                    {webHistory.map((entry) => (
                      <tr key={entry.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          <div className="max-w-xs truncate">{entry.url}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          <div className="max-w-xs truncate">
                            {entry.title || "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center">
                            {(() => {
                              const browserName = (
                                entry.browser || ""
                              ).toLowerCase();
                              let iconName = "default.png";

                              if (browserName.includes("chrome"))
                                iconName = "chrome.png";
                              else if (browserName.includes("firefox"))
                                iconName = "firefox.png";
                              else if (browserName.includes("edge"))
                                iconName = "edge.png";
                              else if (browserName.includes("opera gx"))
                                iconName = "opera_gx.png";
                              else if (browserName.includes("opera"))
                                iconName = "opera.png";
                              else if (browserName.includes("safari"))
                                iconName = "safari.png";
                              else if (browserName.includes("brave"))
                                iconName = "brave.png";
                              else if (browserName.includes("vivaldi"))
                                iconName = "vivaldi.png";

                              return (
                                <img
                                  src={`/assets/icons/browsers/${iconName}`}
                                  alt={entry.browser}
                                  className="w-5 h-5 mr-2"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    e.currentTarget.nextElementSibling?.classList.remove(
                                      "hidden",
                                    );
                                  }}
                                />
                              );
                            })()}
                            {/* Fallback Icon */}
                            <GlobeAltIcon className="w-4 h-4 mr-2 text-gray-400 hidden" />
                            {entry.browser || "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {new Date(entry.visit_time).toLocaleString("pt-BR")}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {/* Exibir status com cores diferentes */}
                          {(() => {
                            // Usar campo 'status' se disponível, senão fallback para is_blocked
                            const status =
                              entry.status ||
                              (entry.is_blocked ? "bloqueado" : "normal");

                            if (status === "bloqueado") {
                              return (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                  🔴 Bloqueado
                                </span>
                              );
                            } else if (status === "site_proibido") {
                              return (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                                  🟡 Site Proibido
                                </span>
                              );
                            } else {
                              return (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                  🟢 Normal
                                </span>
                              );
                            }
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === "open-applications" && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Softwares Abertos
              </h3>
            </div>

            {/* Filters */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6 dark:bg-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nome do Processo
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={appProcessName}
                      onChange={(e) => setAppProcessName(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" && filterOpenApplications()
                      }
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                      placeholder="Nome do processo..."
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Título da Janela
                  </label>
                  <input
                    type="text"
                    value={appWindowTitle}
                    onChange={(e) => setAppWindowTitle(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && filterOpenApplications()
                    }
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    placeholder="Título da janela..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Data Inicial
                  </label>
                  <input
                    type="date"
                    value={appStartDate}
                    onChange={(e) => setAppStartDate(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && filterOpenApplications()
                    }
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Data Final
                  </label>
                  <input
                    type="date"
                    value={appEndDate}
                    onChange={(e) => setAppEndDate(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && filterOpenApplications()
                    }
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-between items-center">
                <button
                  onClick={filterOpenApplications}
                  disabled={isLoadingApps}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingApps ? "Carregando..." : "Filtrar"}
                </button>
                {appTotal > 0 && (
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Total: {appTotal} registro(s)
                  </span>
                )}
              </div>
            </div>

            {isLoadingApps ? (
              <div className="flex justify-center items-center h-64">
                <LoadingSpinner size="lg" />
              </div>
            ) : !openApplications ||
              !Array.isArray(openApplications) ||
              openApplications.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">
                Nenhum registro de software aberto encontrado
              </p>
            ) : (
              <>
                {/* Resumo de Tempo de Uso por Processo */}
                <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-lg p-4 border border-blue-200 dark:border-gray-600">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                    <svg
                      className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                    Resumo de Uso por Aplicação
                  </h4>

                  {(() => {
                    // Agrupa processos e soma tempos
                    const processGroups: Record<
                      string,
                      { totalSeconds: number; count: number }
                    > = {};

                    openApplications.forEach((app) => {
                      const processName = app.process_name;
                      const duration = Number(app.duration_seconds) || 0;

                      if (!processGroups[processName]) {
                        processGroups[processName] = {
                          totalSeconds: 0,
                          count: 0,
                        };
                      }

                      processGroups[processName].totalSeconds += duration;
                      processGroups[processName].count += 1;
                    });

                    // Converte para array e ordena por tempo total (decrescente)
                    const sortedProcesses = Object.entries(processGroups)
                      .map(([name, data]) => ({
                        name,
                        totalSeconds: data.totalSeconds,
                        count: data.count,
                        formatted: formatDuration(data.totalSeconds),
                      }))
                      .sort((a, b) => b.totalSeconds - a.totalSeconds); // Ordenar por TEMPO, sem limite

                    const totalSessions = Object.values(
                      processGroups,
                    ).reduce((acc, curr) => acc + curr.count, 0);
                    const totalTime = Object.values(processGroups).reduce(
                      (acc, curr) => acc + curr.totalSeconds,
                      0,
                    );

                    // Dynamic height: 40px per item or min 400px
                    const rowHeight = 40;
                    const chartHeight = Math.max(
                      sortedProcesses.length * rowHeight,
                      400,
                    );

                    return (
                      <div className="flex flex-col w-full">
                        <div className="flex justify-end space-x-4 mb-2 text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
                          <span>
                            Total de Sessões:{" "}
                            <strong>{totalSessions}</strong>
                          </span>
                          <span>
                            Tempo Total:{" "}
                            <strong>{formatDuration(totalTime)}</strong>
                          </span>
                        </div>

                        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar w-full">
                          <div
                            style={{ width: "100%", height: chartHeight }}
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={sortedProcesses}
                                layout="vertical"
                                margin={{
                                  top: 5,
                                  right: 30,
                                  left: 100,
                                  bottom: 5,
                                }}
                                barCategoryGap="20%"
                                barGap={2}
                              >
                                <CartesianGrid
                                  strokeDasharray="3 3"
                                  horizontal={false}
                                />
                                <XAxis type="number" hide />
                                <YAxis
                                  dataKey="name"
                                  type="category"
                                  width={150}
                                  tick={({ x, y, payload }) => (
                                    <g transform={`translate(${x},${y})`}>
                                      <title>{payload.value}</title>
                                      <text
                                        x={0}
                                        y={0}
                                        dy={4}
                                        textAnchor="end"
                                        fill="currentColor"
                                        fontSize={12}
                                        className="cursor-help text-gray-600 dark:text-gray-400"
                                      >
                                        {payload.value.length > 20
                                          ? payload.value.substring(0, 20) +
                                            "..."
                                          : payload.value}
                                      </text>
                                    </g>
                                  )}
                                />
                                <Tooltip
                                  cursor={{ fill: "rgba(0, 0, 0, 0.1)" }}
                                  content={({ active, payload }) => {
                                    if (
                                      active &&
                                      payload &&
                                      payload.length
                                    ) {
                                      const data = payload[0].payload;
                                      return (
                                        <div className="bg-white p-2 border border-gray-300 rounded shadow text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white z-50">
                                          <p className="font-semibold">
                                            {data.name}
                                          </p>
                                          <p>Tempo: {data.formatted}</p>
                                          <p>Sessões: {data.count}</p>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <Bar
                                  dataKey="totalSeconds"
                                  fill="#3B82F6"
                                  radius={[0, 4, 4, 0]}
                                  barSize={20}
                                >
                                  {sortedProcesses.map((entry, index) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill="#3B82F6"
                                    />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Tabela Detalhada */}
                <div className="overflow-y-auto max-h-[600px] shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Processo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Título da Janela
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Aberto em
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Fechado em
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Tempo de Uso
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                      {Array.isArray(openApplications) &&
                        openApplications.map((app) => (
                          <tr key={app.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {app.process_name}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                              <div
                                className="max-w-md truncate"
                                title={app.window_title}
                              >
                                {app.window_title}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(app.opened_at).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {app.closed_at
                                ? new Date(app.closed_at).toLocaleString("pt-BR", {
                                    timeZone: "America/Sao_Paulo",
                                  })
                                : "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {app.duration_formatted ||
                                formatDuration(app.duration_seconds)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {appTotalPages > 1 && (
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      Página {appCurrentPage} de {appTotalPages}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => loadOpenApplications(appCurrentPage - 1)}
                        disabled={appCurrentPage === 1 || isLoadingApps}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => loadOpenApplications(appCurrentPage + 1)}
                        disabled={
                          appCurrentPage === appTotalPages || isLoadingApps
                        }
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                      >
                        Próxima
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === "location" && (
          <div className="p-4">
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Recalcular Localização Manualmente
                <span className="ml-2 text-xs text-gray-500 font-normal">
                  (Use se a localização automática estiver incorreta)
                </span>
              </label>
              <div className="flex gap-3 items-center">
                <select
                  value={locationService}
                  onChange={(e) => setLocationService(e.target.value)}
                  className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="auto">
                    Automático (GPS → IP API → MaxMind)
                  </option>
                  <option value="gps">Forçar GPS (Windows Location)</option>
                  <option value="ip-api">Forçar IP API (ip-api.com)</option>
                  <option value="maxmind">Forçar MaxMind (Local DB)</option>
                </select>
                <button
                  onClick={handleRecalculateLocation}
                  disabled={isRecalculating}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isRecalculating ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Recalculando...
                    </>
                  ) : (
                    <>
                      <MapPinIcon className="w-4 h-4 mr-2" />
                      Recalcular
                    </>
                  )}
                </button>
              </div>
            </div>

            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Histórico de Localização
            </h3>
            {locations.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">
                Nenhum dado de localização disponível
              </p>
            ) : (
              <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                {locations.map((location) => (
                  <div
                    key={location.id}
                    className="border border-gray-200 rounded-lg p-4 dark:border-gray-700"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <MapPinIcon className="w-5 h-5 text-gray-400 mr-2" />
                        <span className="font-medium text-gray-900 dark:text-white">
                          {location.latitude}, {location.longitude}
                        </span>
                      </div>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(location.timestamp).toLocaleString("pt-BR")}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 ml-7 mb-2">
                      <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        Método: {getLocationMethodLabel(location.source)}
                      </span>
                      {location.ip_address && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          IP: {location.ip_address}
                        </span>
                      )}
                    </div>
                    {location.address && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 ml-7 mb-2">
                        {location.address}
                      </p>
                    )}
                    <div className="ml-7 mt-2">
                      <a
                        href={`https://www.google.com/maps?q=${location.latitude},${location.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <MapPinIcon className="w-4 h-4 mr-1" />
                        Ver no Google Maps
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleUpdateMachine}>
                <div className="bg-white dark:bg-gray-800 px-3 pt-4 pb-3 sm:p-4 sm:pb-3">
                  <h3 className="text-sm leading-5 font-medium text-gray-900 dark:text-white mb-3">
                    Editar Máquina
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Observação
                      </label>
                      <textarea
                        value={observation}
                        onChange={(e) => setObservation(e.target.value)}
                        rows={4}
                        maxLength={2000}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="Adicione uma observação sobre esta máquina..."
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {observation.length}/2000 caracteres
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {isSaving ? "Salvando..." : "Salvar"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteMachine}
        title="Excluir Máquina"
        message={`Tem certeza que deseja excluir a máquina "${machine.name}"? Ela será movida para a lixeira e poderá ser restaurada posteriormente.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={isDeleting}
      />
      {machine && (
        <EditBranchModal
          isOpen={isEditBranchModalOpen}
          onClose={() => setIsEditBranchModalOpen(false)}
          onSuccess={() => {
            loadMachineData();
            setIsEditBranchModalOpen(false);
          }}
          machineId={machine.id}
          currentBranchId={machine.branch_id || null}
          machineName={machine.name || machine.agent_id}
        />
      )}
    </div>
  );
}

// Helper function to get location method label
function getLocationMethodLabel(source?: string): string {
  switch (source) {
    case "gps":
      return "GPS";
    case "gps_forced":
      return "GPS (Forçado)";
    case "ip_api":
      return "API via IP";
    case "ip_maxmind":
      return "GeoIP (MaxMind)";
    case "ip_forced":
      return "IP (Forçado)";
    case "manual":
      return "Manual";
    default:
      return source ? source : "Desconhecido";
  }
}

// Helper function to format duration
function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes < 60) {
    if (remainingSeconds > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes > 0) {
    return `${hours}h ${remainingMinutes}m`;
  }
  return `${hours}h`;
}
